import { useState, useEffect, useCallback } from 'react';
import { storeWebsiteApi, cmsPagesApi } from '../services/storeWebsiteApi';
import { useNotification } from '../../../context/NotificationContext';

export const useStoreWebsite = (locationId) => {
  const [website, setWebsite] = useState(null);
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const notification = useNotification();

  const platformDomain = process.env.REACT_APP_PLATFORM_DOMAIN || 'adgro.app';

  // Fetch website data
  const fetchWebsite = useCallback(async () => {
    if (!locationId) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await storeWebsiteApi.getByLocation(locationId);
      setWebsite(data);

      // Fetch pages if website exists
      if (data?._id) {
        const pagesData = await cmsPagesApi.getByWebsite(data._id);
        setPages(pagesData);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        // Website doesn't exist yet - that's okay
        setWebsite(null);
        setPages([]);
      } else {
        setError(err.message || 'Failed to load website settings');
        notification.error('Failed to load website settings');
      }
    } finally {
      setIsLoading(false);
    }
  }, [locationId, notification]);

  useEffect(() => {
    fetchWebsite();
  }, [fetchWebsite]);

  // Create website
  const createWebsite = async (data) => {
    try {
      setIsSaving(true);
      const newWebsite = await storeWebsiteApi.create({
        location_id: locationId,
        ...data,
      });
      setWebsite(newWebsite);

      // Server creates default pages automatically, just fetch them
      const pagesData = await cmsPagesApi.getByWebsite(newWebsite._id);
      setPages(pagesData);

      notification.success('Website created successfully');
      return newWebsite;
    } catch (err) {
      notification.error(err.response?.data?.message || 'Failed to create website');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  // Update website settings
  const updateWebsite = async (data) => {
    if (!website?._id) return;

    try {
      setIsSaving(true);
      const updated = await storeWebsiteApi.update(website._id, data);
      setWebsite(updated);
      notification.success('Website settings saved');
      return updated;
    } catch (err) {
      notification.error(err.response?.data?.message || 'Failed to save settings');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  // Delete website
  const deleteWebsite = async () => {
    if (!website?._id) return;

    try {
      setIsSaving(true);
      await storeWebsiteApi.delete(website._id);
      setWebsite(null);
      setPages([]);
      notification.success('Website deleted');
    } catch (err) {
      notification.error(err.response?.data?.message || 'Failed to delete website');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle publish status
  const togglePublish = async () => {
    if (!website?._id) return;

    try {
      setIsSaving(true);
      const updated = await storeWebsiteApi.update(website._id, {
        is_published: !website.is_published,
      });
      setWebsite(updated);
      notification.success(updated.is_published ? 'Website published' : 'Website unpublished');
      return updated;
    } catch (err) {
      notification.error('Failed to update publish status');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  // Verify custom domain
  const verifyDomain = async () => {
    if (!website?._id) return;

    try {
      setIsSaving(true);
      const result = await storeWebsiteApi.verifyDomain(website._id);
      if (result.verified) {
        setWebsite({ ...website, custom_domain_verified: true });
        notification.success('Domain verified successfully');
      } else {
        notification.warning('Domain verification pending. Please check your DNS settings.');
      }
      return result;
    } catch (err) {
      notification.error(err.response?.data?.message || 'Domain verification failed');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  // Get public URL
  const getPublicUrl = () => {
    if (!website) return null;
    return storeWebsiteApi.getPublicUrl(website, platformDomain);
  };

  // Page operations
  const createPage = async (pageData) => {
    if (!website?._id) return;

    try {
      setIsSaving(true);
      const newPage = await cmsPagesApi.create({
        website_id: website._id,
        ...pageData,
      });
      setPages([...pages, newPage]);
      notification.success('Page created');
      return newPage;
    } catch (err) {
      notification.error('Failed to create page');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const updatePage = async (pageId, pageData) => {
    try {
      setIsSaving(true);
      const updated = await cmsPagesApi.update(pageId, pageData);
      setPages(pages.map((p) => (p._id === pageId ? updated : p)));
      notification.success('Page saved');
      return updated;
    } catch (err) {
      notification.error('Failed to save page');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const deletePage = async (pageId) => {
    try {
      setIsSaving(true);
      await cmsPagesApi.delete(pageId);
      setPages(pages.filter((p) => p._id !== pageId));
      notification.success('Page deleted');
    } catch (err) {
      notification.error('Failed to delete page');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const togglePagePublish = async (pageId) => {
    const page = pages.find((p) => p._id === pageId);
    if (!page) return;

    try {
      setIsSaving(true);
      if (page.is_published) {
        await cmsPagesApi.unpublish(pageId);
      } else {
        await cmsPagesApi.publish(pageId);
      }
      setPages(
        pages.map((p) =>
          p._id === pageId ? { ...p, is_published: !p.is_published } : p
        )
      );
      notification.success(page.is_published ? 'Page unpublished' : 'Page published');
    } catch (err) {
      notification.error('Failed to update page status');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    website,
    pages,
    isLoading,
    isSaving,
    error,
    platformDomain,

    // Website operations
    createWebsite,
    updateWebsite,
    deleteWebsite,
    togglePublish,
    verifyDomain,
    getPublicUrl,
    refetch: fetchWebsite,

    // Page operations
    createPage,
    updatePage,
    deletePage,
    togglePagePublish,
  };
};

export default useStoreWebsite;
